import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';
import loginAPI, { userSession } from '@/api/modules/login.api.js';
import appAPI, { currentDomainOrganisation } from '@/api/modules/app.api.js';
import i18n from '@/common/i18n/i18n.js';
import toastr from 'toastr';
import Config from '@/services/config.service';

const appConfig = new Config();

import accountsRoutes from '@/app/accounts/routes.js';
import commonRoutes from '@/common/routes.js';
import datasetRoutes from '@/app/dataset/routes.js';
import indexerRoutes from '@/app/indexer/routes.js';
import jurisductionRoutes from '@/app/jurisdiction/routes.js';
import logsRoutes from '@/app/logs/routes.js';
import organisationRoutes from '@/app/organisation/routes.js';
import tasksRoutes from '@/app/tasks/routes.js';
import userRoutes from '@/app/user/routes.js';
import usergroupRoutes from '@/app/usergroup/routes.js';

Vue.use(VueRouter);

const ACCESS_ROLES = {
  DELEGATED_ADMIN: 2,
  EDITOR: 1,
};

function normalizePathname(pathname) {
  const normalizedPath = decodeURI(pathname)
    // Replaces repeated slashes in the URL.
    .replaceAll(/\/+/g, '/')
    // Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
    // Note: Missing native IE support, may want to skip this step.
    .normalize();
  return `${normalizedPath}/`;
}

function redirectWithDefaultLocale(to, pathFromLocale) {
  const segment = pathFromLocale.endsWith('/') ? pathFromLocale : normalizePathname(pathFromLocale);
  return {
    path: `${appConfig.i18n.defaultLocale}${segment}`,
    query: to.query,
  };
}

function getLocaleBranchRedirect(to, locale, supportedLocales) {
  if (!locale) {
    return redirectWithDefaultLocale(to, to.path);
  }
  if (locale.length !== 2) {
    return redirectWithDefaultLocale(to, to.path);
  }
  if (!supportedLocales.includes(locale)) {
    return redirectWithDefaultLocale(to, to.path.slice(3));
  }
  if (!to.path.endsWith('/')) {
    return {
      path: normalizePathname(to.path),
      query: to.query,
    };
  }
  return null;
}

function classicAccess(role) {
  return userSession.isSuperuser() || userSession.getUsergroupRoles().some((el) => el.role >= role);
}
function saasAccess(role) {
  return userSession.isSuperuser()
    || (
      currentDomainOrganisation.value
      && userSession.getUsergroupRoles().length > 0
      && userSession.getUsergroupRoles().some((el) => el.role >= role && el.usergroup.id === currentDomainOrganisation.value.id)
    );
}

function resolveAfterAuthNavigation(to, next) {
  const accessCheck = appConfig.isSaas ? saasAccess : classicAccess;
  const locale = to.params.locale || appConfig.i18n.defaultLocale;

  if (to.path.includes('account')) {
    if (accessCheck(ACCESS_ROLES.DELEGATED_ADMIN)) {
      return next();
    }
    return next({ name: '403Page', params: { locale } });
  }

  if (to.path.includes('data')) {
    if (accessCheck(ACCESS_ROLES.EDITOR)) {
      return next();
    }
    return next({ name: '403Page', params: { locale } });
  }

  return next();
}

const routes = [
  {
    path: '/:locale',
    component: () => import('@/common/views/Base.vue'),
    beforeEnter: async (to, from, next) => {
      const locale = to.params.locale;
      const supportedLocales = appConfig.i18n.supportedLocales;

      const localeRedirect = getLocaleBranchRedirect(to, locale, supportedLocales);
      if (localeRedirect) {
        return next(localeRedirect);
      }

      i18n.locale = locale;

      if (store.state.reloadIntervalId) {
        store.commit('CLEAR_RELOAD_INTERVAL_ID');
      }
      if (!userSession.isUserAuthenticated) {
        await loginAPI.authenticate();
      }
      if (appConfig.isSaas && !currentDomainOrganisation.value) {
        await appAPI.getCurrentDomainOrganisation();
      }
      resolveAfterAuthNavigation(to, next);
    },
    children: [
      ...commonRoutes,
      // Gestion des comptes
      ...accountsRoutes,
      // Utilisateurs
      ...userRoutes,
      // Organisations
      ...organisationRoutes,
      //Usergroups
      ...usergroupRoutes,
      // Indexers
      ...indexerRoutes,
      // Jurisdictions
      ...jurisductionRoutes,
      // Jeux de données
      ...datasetRoutes,
      // Logs
      ...logsRoutes,
      // Tasks
      ...tasksRoutes,
    ]
  },
  {
    path: '*',
    name: 'Fallback',
    beforeEnter: (to, from, next) => {
      const matchedRoutes = router.resolve(
        `${appConfig.i18n.defaultLocale}${to.path.endsWith('/') ?
          to.path :
          normalizePathname(to.path)}`
      ).resolved.matched;
      if (
        matchedRoutes && !matchedRoutes.some(el => el.name === 'Fallback')
      ) {
        return next({
          path: `${appConfig.i18n.defaultLocale}${to.path.endsWith('/')
            ? to.path
            : normalizePathname(to.path)}`,
          query: to.query
        });
      }
      return next({
        path: 'not-found/',
      });
    }
  },

];

const router = new VueRouter({
  routes,
  scrollBehavior: (to) => {
    if (to.hash) {
      return new Promise((resolve) => {
        const startTime = Date.now();
        const maxWaitTime = 5000; // Délai d'attente maximal en millisecondes (par exemple, 5 secondes)
  
        const waitForElement = () => {
          const element = document.querySelector(to.hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            resolve();
          } else {
            const elapsedTime = Date.now() - startTime;
            if (elapsedTime >= maxWaitTime) {
              resolve({ x: 0, y: 0 }); // Retourner en haut si le délai est dépassé
            } else {
              setTimeout(waitForElement, 100); // Réessayer après 100 ms
            }
          }
        };
  
        waitForElement();
      });
    }
  },
  mode: 'history',
  base: process.env.VUE_APP_BASE_PATH || '',
});

router.beforeEach((to, from, next) => {
  toastr.clear();

  if (
    appConfig.isSaas &&
    to.name &&
    appConfig.saasBlockedRouteNames.includes(to.name)
  ) {
    console.log('to.name', to.name);
    console.log('appConfig.saasBlockedRouteNames', appConfig.saasBlockedRouteNames);
    return next({
      name: '403Page',
      params: {
        locale: to.params.locale || appConfig.i18n.defaultLocale,
      },
    });
  }

  next();
});

export default router;
